import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  ListGroup,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import { useSupplierAssessments } from '../hooks/useSupplierAssessments';
import AssessmentScoreBar from '../components/assessment/AssessmentScoreBar';
import { useRecordPageView } from '../hooks/useRecordPageView';

const MyCompliance = () => {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const { complianceOverview, overviewLoading, error } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  useRecordPageView();

  const overview = complianceOverview;

  return (
    <Container fluid>
      <h3 className="mb-4">My Compliance</h3>

      <Col md={8} className="mb-4">
        <p>
          Welcome to your Compliance Dashboard! Here you can track your
          organisation's compliance performance based on the assessments you've
          completed. This dashboard provides insights into your strengths and
          areas for improvement, helping you stay on top of your compliance
          obligations.
        </p>
      </Col>

      {overviewLoading && <p>Loading Compliance...</p>}
      {error && <p className="text-danger">Error: {error}</p>}

      {overview && overview.assessment_count > 0 && (
        <>
          {/* KPI SUMMARY */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Average Compliance</Card.Title>
                  <h2>{overview.average_compliance_percentage}%</h2>
                  <ProgressBar
                    now={overview.average_compliance_percentage}
                    variant={
                      overview.average_compliance_percentage >= 75
                        ? 'success'
                        : overview.average_compliance_percentage >= 50
                          ? 'warning'
                          : 'danger'
                    }
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Assessments Completed</Card.Title>
                  <h2>{overview.assessment_count}</h2>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>File Completion</Card.Title>
                  <h2>{overview.file_overview.completion_percentage}%</h2>
                  <ProgressBar
                    now={overview.file_overview.completion_percentage}
                    variant={
                      overview.file_overview.completion_percentage >= 90
                        ? 'success'
                        : 'warning'
                    }
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* FILE OVERVIEW */}
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>File Overview</Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      Total File Questions:{' '}
                      <strong>
                        {overview.file_overview.total_file_questions}
                      </strong>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Files Provided:{' '}
                      <strong>{overview.file_overview.files_provided}</strong>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Files Missing:{' '}
                      <strong>{overview.file_overview.files_missing}</strong>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Missing Files</Card.Title>

                  {overview.file_overview.missing_files.length === 0 ? (
                    <p className="text-success mb-0">
                      All required files provided 🎉
                    </p>
                  ) : (
                    <ListGroup>
                      {overview.file_overview.missing_files.map((file, idx) => (
                        <ListGroup.Item key={idx}>
                          <div className="fw-bold">{file.section_title}</div>
                          <div className="text-muted">
                            {file.question_title}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION PERFORMANCE */}
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Strong Sections</Card.Title>

                  {overview.section_performance.strong_sections.map(
                    (section, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="mb-1 fw-semibold">{section.title}</div>
                        <AssessmentScoreBar
                          value={section.average_percentage}
                        />
                      </div>
                    ),
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Weak Sections</Card.Title>

                  {overview.section_performance.weak_sections.map(
                    (section, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="mb-1 fw-semibold">{section.title}</div>
                        <AssessmentScoreBar
                          value={section.average_percentage}
                        />
                      </div>
                    ),
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* HIGH-LEVEL FINDINGS */}
          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Card.Title>High-Level Findings</Card.Title>
                  <ListGroup variant="flush">
                    {overview.high_level_findings.map((finding, idx) => (
                      <ListGroup.Item key={idx}>{finding}</ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default MyCompliance;
